import { useState, useEffect, useRef, type CSSProperties } from 'react'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { useAuthStore } from '../stores/authStore'
import { api, apiPut, apiPost, API_BASE } from '../lib/api'
import '../styles/ui.css'
import UserAvatar from '../components/UserAvatar'
import { ConfirmDialog } from '../components/ConfirmDialog'
import { InlineError } from '../components/InlineError'

type User = {
    id: string
    email: string
    displayName: string
    avatarUrl: string | null
    role: string
    roleId?: string
    roleName?: string
    isActive: boolean
    isHeadOffice?: boolean
    createdAt: string
    lastLoginAt: string | null
    storeCount: number
}

type UserDetail = User & {
    assignments: { storeId: string; storeName: string; classificationId: string | null; classification: string | null; assignedAt: string }[]
}

type ActiveStore = {
    legacyStoreId: string
    name: string
    city: string | null
    province: string | null
}

type Classification = {
    id: string
    name: string
    description: string | null
}

type ClassificationsResponse = {
    classifications: Classification[]
    allPermissions: { flag: number; label: string }[]
}

type StoreEntry = {
    storeId: string
    classificationId: string | null
}

type UsersResponse = { total: number; page: number; pageSize: number; users: User[] }

/** A pending invitation — someone invited but not yet signed in, so not a User row. */
type Invitation = {
    id: string
    email: string
    token: string
    status: string
    message: string | null
    createdAt: string
    expiresAt: string
    lastSentAt: string | null
    invitedBy: string | null
    storeIds: string[]
    storeNames: string[]
    isExpired: boolean
}

// A row action awaiting confirmation. One state drives a single shared ConfirmDialog.
type PendingConfirm =
    | { kind: 'impersonate'; user: User }
    | { kind: 'deactivate'; user: User }
    | { kind: 'reactivate'; user: User }
    | { kind: 'delete'; user: User }
    | { kind: 'revokeInvite'; invitation: Invitation }

type SortField = 'firstName' | 'lastName' | 'email' | 'role'

function roleBadgeClass(role: string) {
    return `badge badge-${role.toLowerCase().replace('store', 'store')}`
}

const filterSelectStyle: CSSProperties = {
    width: 'auto', minWidth: 110,
    fontSize: 'var(--text-xs)', padding: 'var(--space-1) var(--space-2)'
}

type MenuItem = { icon: string; label: string; danger?: boolean; onClick: () => void }

/**
 * The per-row [⋯] menu — user rows get every action except impersonate, invitation
 * rows get their own set. Rendered position:fixed because the table wrapper clips
 * overflow; dismissed on outside click, Escape, scroll, or picking an item.
 * `sections` are rendered as divider-separated groups.
 */
function RowActionsMenu({ sections }: { sections: MenuItem[][] }) {
    const [open, setOpen] = useState(false)
    const [pos, setPos] = useState<{ right: number; top?: number; bottom?: number }>({ right: 0 })
    const wrapRef = useRef<HTMLDivElement>(null)
    const btnRef = useRef<HTMLButtonElement>(null)

    const toggle = () => {
        if (open) { setOpen(false); return }
        const rect = btnRef.current!.getBoundingClientRect()
        const estimatedHeight = 44 * sections.flat().length + 16 // decides whether to flip upward
        const openUp = rect.bottom + estimatedHeight > window.innerHeight
        setPos({
            right: window.innerWidth - rect.right,
            ...(openUp
                ? { bottom: window.innerHeight - rect.top + 4 }
                : { top: rect.bottom + 4 })
        })
        setOpen(true)
    }

    useEffect(() => {
        if (!open) return
        const onDown = (e: MouseEvent) => {
            if (wrapRef.current && !wrapRef.current.contains(e.target as Node)) setOpen(false)
        }
        const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') setOpen(false) }
        const onScroll = () => setOpen(false) // fixed-positioned: the anchor moves away on scroll
        document.addEventListener('mousedown', onDown)
        document.addEventListener('keydown', onKey)
        window.addEventListener('scroll', onScroll, true)
        window.addEventListener('resize', onScroll)
        return () => {
            document.removeEventListener('mousedown', onDown)
            document.removeEventListener('keydown', onKey)
            window.removeEventListener('scroll', onScroll, true)
            window.removeEventListener('resize', onScroll)
        }
    }, [open])

    const pick = (action: () => void) => { setOpen(false); action() }

    return (
        <div ref={wrapRef} style={{ position: 'relative' }}>
            <button
                ref={btnRef}
                className="btn-icon"
                title="More actions"
                aria-haspopup="menu"
                aria-expanded={open}
                onClick={toggle}
            >
                ⋯
            </button>
            {open && (
                <div
                    className="row-actions-menu"
                    role="menu"
                    style={{ ...pos, transformOrigin: pos.top !== undefined ? 'top right' : 'bottom right' }}
                >
                    {sections.map((items, i) => (
                        <div className="dropdown-section" key={i}>
                            {items.map(item => (
                                <button
                                    key={item.label}
                                    className={`dropdown-item${item.danger ? ' danger' : ''}`}
                                    role="menuitem"
                                    onClick={() => pick(item.onClick)}
                                >
                                    <span className="dropdown-item-icon">{item.icon}</span>{item.label}
                                </button>
                            ))}
                        </div>
                    ))}
                </div>
            )}
        </div>
    )
}

export default function AdminUsersPage() {
    const token = useAuthStore(s => s.accessToken)
    const qc = useQueryClient()
    const [page, setPage] = useState(1)
    const [search, setSearch] = useState('')
    const [roleFilter, setRoleFilter] = useState('')
    const [storeFilter, setStoreFilter] = useState('')
    const [statusFilter, setStatusFilter] = useState('')
    const [sortBy, setSortBy] = useState<SortField>('firstName')
    const [sortDir, setSortDir] = useState<'asc' | 'desc'>('asc')
    const [roleModal, setRoleModal] = useState<User | null>(null)
    const [newRoleId, setNewRoleId] = useState('')
    const [storeModal, setStoreModal] = useState<User | null>(null)
    const [storeEntries, setStoreEntries] = useState<StoreEntry[]>([])
    const [confirmAction, setConfirmAction] = useState<PendingConfirm | null>(null)
    const [actionError, setActionError] = useState<string | null>(null)
    const [actionNotice, setActionNotice] = useState<string | null>(null)

    // Invite dialog
    const [inviteOpen, setInviteOpen] = useState(false)
    const [inviteEmail, setInviteEmail] = useState('')
    const [inviteMessage, setInviteMessage] = useState('')
    const [inviteStores, setInviteStores] = useState<StoreEntry[]>([])
    const [inviteError, setInviteError] = useState<string | null>(null)

    const canInvite = useAuthStore.getState().hasPermission('users.invite')

    const authHeaders = { Authorization: `Bearer ${token}` }

    const { data, isLoading } = useQuery<UsersResponse>({
        queryKey: ['admin-users', page, search, roleFilter, storeFilter, statusFilter, sortBy, sortDir],
        queryFn: async () => {
            const params = new URLSearchParams({ page: String(page), pageSize: '25', sortBy, sortDir })
            if (search) params.set('search', search)
            if (roleFilter) params.set('roleId', roleFilter)
            if (storeFilter) params.set('storeId', storeFilter)
            if (statusFilter) params.set('status', statusFilter)
            const res = await fetch(API_BASE + `/api/admin/users?${params}`, { headers: authHeaders })
            return res.json()
        },
        staleTime: 10_000
    })

    // Role list for the filter dropdown (unfiltered, unlike roles/assignable)
    const { data: filterOptions } = useQuery<{ roles: { id: string; name: string }[] }>({
        queryKey: ['user-filter-options'],
        queryFn: () => api('/api/admin/users/filter-options', token),
        staleTime: 60_000
    })

    // Fetch user detail (including assignments) when store modal opens
    const { data: userDetail } = useQuery<UserDetail>({
        queryKey: ['admin-user-detail', storeModal?.id],
        queryFn: () => api(`/api/admin/users/${storeModal!.id}`, token),
        enabled: !!storeModal
    })

    // Fetch all active stores — used by both the store filter and the assignment modal
    const { data: allStores } = useQuery<ActiveStore[]>({
        queryKey: ['admin-stores-active'],
        queryFn: () => api('/api/admin/stores/active', token),
        staleTime: 60_000
    })

    // Fetch classifications (endpoint returns { classifications: [...], allPermissions: [...] })
    const { data: classificationsData } = useQuery<ClassificationsResponse>({
        queryKey: ['classifications'],
        queryFn: () => api('/api/admin/classifications', token),
        enabled: !!storeModal || inviteOpen
    })
    const classifications = classificationsData?.classifications ?? []

    // Pending invitations — rendered as "Invited" rows above the real users.
    const { data: invitationData } = useQuery<{ invitations: Invitation[] }>({
        queryKey: ['admin-invitations'],
        queryFn: () => api('/api/admin/invitations', token),
        staleTime: 10_000
    })

    // Sync store entries when userDetail loads
    useEffect(() => {
        if (userDetail?.assignments) {
            setStoreEntries(userDetail.assignments.map(a => ({
                storeId: a.storeId,
                classificationId: a.classificationId ?? null
            })))
        }
    }, [userDetail])

    const { data: assignableRoles } = useQuery<{ id: string; name: string; rank: number; isSystem: boolean; isSuperAdmin: boolean }[]>({
        queryKey: ['assignable-roles'],
        queryFn: async () => {
            const res = await fetch(API_BASE + '/api/admin/roles/assignable', { headers: authHeaders })
            return res.ok ? res.json() : []
        }
    })

    const changeRole = useMutation({
        mutationFn: async ({ userId, roleId }: { userId: string; roleId: string }) => {
            const res = await fetch(API_BASE + `/api/admin/users/${userId}/role`, {
                method: 'PUT',
                headers: { ...authHeaders, 'Content-Type': 'application/json' },
                body: JSON.stringify({ roleId })
            })
            if (!res.ok) throw new Error((await res.json()).error ?? 'Failed')
        },
        onSuccess: () => {
            qc.invalidateQueries({ queryKey: ['admin-users'] })
            setRoleModal(null)
        }
    })

    const deactivate = useMutation({
        mutationFn: async (userId: string) => {
            await apiPost(`/api/admin/users/${userId}/deactivate`, token)
        },
        onSuccess: () => qc.invalidateQueries({ queryKey: ['admin-users'] }),
        onError: (err: Error) => setActionError(`Deactivate failed: ${err.message}`)
    })

    const reactivate = useMutation({
        mutationFn: async (userId: string) => {
            await apiPost(`/api/admin/users/${userId}/reactivate`, token)
        },
        onSuccess: () => qc.invalidateQueries({ queryKey: ['admin-users'] }),
        onError: (err: Error) => setActionError(`Reactivate failed: ${err.message}`)
    })

    const deleteUser = useMutation({
        mutationFn: async (userId: string) => {
            const res = await fetch(API_BASE + `/api/admin/users/${userId}`, {
                method: 'DELETE', headers: authHeaders
            })
            if (!res.ok) {
                let msg = 'Failed'
                try { msg = (await res.json()).error ?? msg } catch { /* */ }
                throw new Error(msg)
            }
        },
        onSuccess: () => qc.invalidateQueries({ queryKey: ['admin-users'] }),
        onError: (err: Error) => setActionError(`Delete failed: ${err.message}`)
    })

    const saveStores = useMutation({
        mutationFn: ({ userId, stores }: { userId: string; stores: StoreEntry[] }) =>
            apiPut(`/api/admin/users/${userId}/stores`, token, { stores }),
        onSuccess: () => {
            qc.invalidateQueries({ queryKey: ['admin-users'] })
            qc.invalidateQueries({ queryKey: ['admin-user-detail'] })
            setStoreModal(null)
        }
    })

    const createInvite = useMutation({
        mutationFn: async () => {
            const res = await fetch(API_BASE + '/api/admin/invitations', {
                method: 'POST',
                headers: { ...authHeaders, 'Content-Type': 'application/json' },
                body: JSON.stringify({
                    email: inviteEmail.trim(),
                    message: inviteMessage.trim() || null,
                    stores: inviteStores.map(s => ({ storeId: s.storeId, classificationId: s.classificationId }))
                })
            })
            const body = await res.json().catch(() => ({}))
            if (!res.ok) throw new Error(body.error ?? 'Could not send the invitation.')
            return body as { message: string; emailSent: boolean }
        },
        onSuccess: (result) => {
            qc.invalidateQueries({ queryKey: ['admin-invitations'] })
            closeInvite()
            setActionNotice(result.message)
        },
        onError: (err: Error) => setInviteError(err.message)
    })

    const resendInvite = useMutation({
        mutationFn: async (invitation: Invitation) => {
            const res = await fetch(API_BASE + `/api/admin/invitations/${invitation.id}/resend`, {
                method: 'POST', headers: authHeaders
            })
            const body = await res.json().catch(() => ({}))
            if (!res.ok) throw new Error(body.error ?? 'Resend failed.')
            return body as { message: string }
        },
        onSuccess: (result) => {
            qc.invalidateQueries({ queryKey: ['admin-invitations'] })
            setActionNotice(result.message)
        },
        onError: (err: Error) => setActionError(`Resend failed: ${err.message}`)
    })

    const revokeInvite = useMutation({
        mutationFn: async (invitation: Invitation) => {
            const res = await fetch(API_BASE + `/api/admin/invitations/${invitation.id}/revoke`, {
                method: 'POST', headers: authHeaders
            })
            const body = await res.json().catch(() => ({}))
            if (!res.ok) throw new Error(body.error ?? 'Revoke failed.')
        },
        onSuccess: () => qc.invalidateQueries({ queryKey: ['admin-invitations'] }),
        onError: (err: Error) => setActionError(`Revoke failed: ${err.message}`)
    })

    const impersonate = useMutation({
        mutationFn: async (targetUser: User) => {
            const res = await fetch(API_BASE + `/api/auth/impersonate/${targetUser.id}`, {
                method: 'POST', headers: authHeaders
            })
            if (!res.ok) {
                let msg = 'Impersonation failed'
                try { msg = (await res.json()).error ?? msg } catch { /* */ }
                throw new Error(msg)
            }
            return { data: await res.json() }
        },
        onSuccess: ({ data }) => {
            const { accessToken, refreshToken, user: impersonatedProfile } = data
            useAuthStore.getState().startImpersonation(
                impersonatedProfile,
                accessToken,
                refreshToken
            )
            qc.clear()
            window.location.href = '/' // Force hard reload to reset all states
        },
        onError: (err: Error) => setActionError(`Impersonate failed: ${err.message}`)
    })

    const toggleStore = (storeId: string) => {
        setStoreEntries(prev => {
            const exists = prev.find(e => e.storeId === storeId)
            if (exists) return prev.filter(e => e.storeId !== storeId)
            return [...prev, { storeId, classificationId: null }]
        })
    }

    const setClassification = (storeId: string, classificationId: string | null) => {
        setStoreEntries(prev =>
            prev.map(e => e.storeId === storeId ? { ...e, classificationId } : e)
        )
    }

    // Opening the invite dialog inherits the store filter, so "filter to Nelson, click
    // Invite" starts with Nelson already ticked.
    const openInvite = () => {
        setInviteEmail('')
        setInviteMessage('')
        setInviteError(null)
        setInviteStores(storeFilter ? [{ storeId: storeFilter, classificationId: null }] : [])
        setInviteOpen(true)
    }
    const closeInvite = () => { setInviteOpen(false); setInviteError(null); createInvite.reset() }

    const toggleInviteStore = (storeId: string) => {
        setInviteStores(prev => prev.find(e => e.storeId === storeId)
            ? prev.filter(e => e.storeId !== storeId)
            : [...prev, { storeId, classificationId: null }])
    }
    const setInviteClassification = (storeId: string, classificationId: string | null) => {
        setInviteStores(prev => prev.map(e => e.storeId === storeId ? { ...e, classificationId } : e))
    }

    const copyInviteLink = async (invitation: Invitation) => {
        const url = `${window.location.origin}/invite/${invitation.token}`
        try {
            await navigator.clipboard.writeText(url)
            setActionNotice(`Invitation link for ${invitation.email} copied to the clipboard.`)
        } catch {
            setActionError(`Could not copy automatically. The link is: ${url}`)
        }
    }

    // Invitations aren't users: they have no role and no active/inactive state, so a role
    // filter excludes them and the status filter treats "invited" as its own bucket. They
    // only show on page 1 — they sit above the paged user rows, not inside the pagination.
    const invitations = (invitationData?.invitations ?? []).filter(inv => {
        if (page !== 1) return false
        if (roleFilter) return false
        if (statusFilter === 'active' || statusFilter === 'inactive') return false
        if (storeFilter && !inv.storeIds.includes(storeFilter)) return false
        if (search && !inv.email.toLowerCase().includes(search.toLowerCase())) return false
        return true
    })
    const showUsers = statusFilter !== 'invited'

    const totalPages = data ? Math.ceil(data.total / data.pageSize) : 1

    const toggleSort = (field: SortField) => {
        if (sortBy === field) {
            setSortDir(d => d === 'asc' ? 'desc' : 'asc')
        } else {
            setSortBy(field)
            setSortDir('asc')
        }
        setPage(1)
    }
    const sortArrow = (field: SortField) => sortBy === field ? (sortDir === 'asc' ? ' ▲' : ' ▼') : ''
    const hasFilters = !!(roleFilter || storeFilter || statusFilter)

    return (
        <>
            <div className="page-header">
                <h1>User Management</h1>
                <p>
                    {data?.total ?? '—'} registered users
                    {(invitationData?.invitations.length ?? 0) > 0 &&
                        ` · ${invitationData!.invitations.length} pending invitation${invitationData!.invitations.length !== 1 ? 's' : ''}`}
                </p>
            </div>

            {actionError && (
                <InlineError message={actionError} onDismiss={() => setActionError(null)} style={{ marginBottom: 'var(--space-4)' }} />
            )}

            {actionNotice && (
                <div style={{
                    display: 'flex', alignItems: 'center', gap: 'var(--space-2)',
                    background: 'var(--color-success-surface)', color: 'var(--color-success)',
                    border: '1px solid var(--color-success)', borderRadius: 'var(--radius-md)',
                    padding: 'var(--space-3)', marginBottom: 'var(--space-4)', fontSize: 'var(--text-sm)'
                }}>
                    <span>✓</span>
                    <span style={{ flex: 1 }}>{actionNotice}</span>
                    <button
                        onClick={() => setActionNotice(null)}
                        style={{ background: 'none', border: 'none', color: 'inherit', cursor: 'pointer', fontSize: 'var(--text-base)' }}
                        aria-label="Dismiss"
                    >✕</button>
                </div>
            )}

            <div className="data-table-wrapper">
                <div className="data-table-toolbar">
                    <h3>All Users</h3>
                    <select
                        className="form-select"
                        style={filterSelectStyle}
                        title="Filter by store"
                        value={storeFilter}
                        onChange={e => { setStoreFilter(e.target.value); setPage(1) }}
                    >
                        <option value="">All stores</option>
                        {(allStores ?? []).map(s => <option key={s.legacyStoreId} value={s.legacyStoreId}>{s.name}</option>)}
                    </select>
                    {hasFilters && (
                        <button
                            className="btn btn-secondary"
                            style={{ fontSize: 'var(--text-xs)', padding: 'var(--space-1) var(--space-2)' }}
                            onClick={() => { setRoleFilter(''); setStoreFilter(''); setStatusFilter(''); setPage(1) }}
                        >
                            ✕ Clear filters
                        </button>
                    )}
                    <div className="search-input">
                        <span>🔍</span>
                        <input
                            placeholder="Search name or email…"
                            value={search}
                            onChange={e => { setSearch(e.target.value); setPage(1) }}
                        />
                    </div>
                    {canInvite && (
                        <button className="btn btn-primary" onClick={openInvite}>
                            ✉ Invite
                        </button>
                    )}
                </div>

                {/* Role + status pills — one pick per group, groups AND together */}
                <div className="filter-pill-row">
                    <span className="filter-pill-label">Role</span>
                    {(filterOptions?.roles ?? []).map(r => (
                        <button
                            key={r.id}
                            className={`filter-pill${roleFilter === r.id ? ' active' : ''}`}
                            onClick={() => { setRoleFilter(f => f === r.id ? '' : r.id); setPage(1) }}
                        >
                            {r.name}
                        </button>
                    ))}
                    <span className="filter-pill-divider" />
                    <span className="filter-pill-label">Status</span>
                    {(['active', 'inactive', 'invited'] as const).map(s => (
                        <button
                            key={s}
                            className={`filter-pill${statusFilter === s ? ' active' : ''}`}
                            onClick={() => { setStatusFilter(f => f === s ? '' : s); setPage(1) }}
                        >
                            {s === 'active' ? 'Active' : s === 'inactive' ? 'Inactive' : 'Invited'}
                        </button>
                    ))}
                </div>

                {isLoading ? (
                    <div className="empty-state"><p>Loading…</p></div>
                ) : (!data?.users.length || !showUsers) && !invitations.length ? (
                    <div className="empty-state">
                        <div className="icon">{statusFilter === 'invited' ? '✉' : '👥'}</div>
                        <h3>{statusFilter === 'invited' ? 'No pending invitations' : 'No users found'}</h3>
                    </div>
                ) : (
                    <table className="data-table">
                        <thead>
                            <tr>
                                <th>
                                    <span
                                        className={`th-sort${sortBy === 'firstName' ? ' active' : ''}`}
                                        title="Sort by first name"
                                        onClick={() => toggleSort('firstName')}
                                    >First{sortArrow('firstName')}</span>
                                    <span style={{ margin: '0 5px' }}>·</span>
                                    <span
                                        className={`th-sort${sortBy === 'lastName' ? ' active' : ''}`}
                                        title="Sort by last name"
                                        onClick={() => toggleSort('lastName')}
                                    >Last{sortArrow('lastName')}</span>
                                    <span style={{ margin: '0 5px' }}>·</span>
                                    <span
                                        className={`th-sort${sortBy === 'email' ? ' active' : ''}`}
                                        title="Sort by email"
                                        onClick={() => toggleSort('email')}
                                    >Email{sortArrow('email')}</span>
                                </th>
                                <th
                                    className={`th-sort${sortBy === 'role' ? ' active' : ''}`}
                                    title="Sort by role"
                                    onClick={() => toggleSort('role')}
                                >Role{sortArrow('role')}</th>
                                <th>Stores</th>
                                <th>Last Login</th>
                                <th>Status</th>
                                <th></th>
                            </tr>
                        </thead>
                        <tbody>
                            {/* Pending invitations — no account exists yet, so no role, avatar or login */}
                            {invitations.map(inv => (
                                <tr key={inv.id}>
                                    <td>
                                        <div className="user-cell">
                                            <div className="user-avatar-fallback" style={{ background: 'var(--color-surface)', color: 'var(--color-text-tertiary)' }}>✉</div>
                                            <div className="user-cell-info">
                                                <div className="name" style={{ color: 'var(--color-text-secondary)' }}>
                                                    Awaiting first sign-in
                                                </div>
                                                <div className="email">{inv.email}</div>
                                            </div>
                                        </div>
                                    </td>
                                    <td style={{ color: 'var(--color-text-tertiary)' }}>—</td>
                                    <td
                                        style={{ color: 'var(--color-text-tertiary)' }}
                                        title={inv.storeNames.join(', ')}
                                    >
                                        {inv.storeNames.length} store{inv.storeNames.length !== 1 ? 's' : ''} pending
                                    </td>
                                    <td style={{ color: 'var(--color-text-tertiary)', fontSize: 'var(--text-xs)' }}>
                                        {inv.isExpired
                                            ? `Expired ${new Date(inv.expiresAt).toLocaleDateString()}`
                                            : `Expires ${new Date(inv.expiresAt).toLocaleDateString()}`}
                                    </td>
                                    <td>
                                        <span className="badge badge-invited">{inv.isExpired ? 'Expired' : 'Invited'}</span>
                                    </td>
                                    <td>
                                        <div className="table-actions">
                                            <RowActionsMenu sections={[
                                                [
                                                    { icon: '✉', label: 'Resend invitation', onClick: () => resendInvite.mutate(inv) },
                                                    { icon: '🔗', label: 'Copy invite link', onClick: () => copyInviteLink(inv) },
                                                ],
                                                [
                                                    { icon: '🚫', label: 'Revoke invitation…', danger: true, onClick: () => setConfirmAction({ kind: 'revokeInvite', invitation: inv }) },
                                                ],
                                            ]} />
                                        </div>
                                    </td>
                                </tr>
                            ))}
                            {showUsers && data?.users.map(u => (
                                <tr key={u.id}>
                                    <td>
                                        <div className="user-cell">
                                            <UserAvatar user={u} />
                                            <div className="user-cell-info">
                                                <div className="name">
                                                    {u.displayName}
                                                    {u.isHeadOffice && (
                                                        <span
                                                            title="Member of the Head Office Google org unit"
                                                            style={{
                                                                marginLeft: 6, fontSize: 10, fontWeight: 800, letterSpacing: '0.5px',
                                                                color: '#fff', background: '#b45309', borderRadius: 4,
                                                                padding: '1px 5px', verticalAlign: 'middle'
                                                            }}
                                                        >HO</span>
                                                    )}
                                                </div>
                                                <div className="email">{u.email}</div>
                                            </div>
                                        </div>
                                    </td>
                                    <td>
                                        <span className={roleBadgeClass(u.role)}>{u.roleName ?? u.role}</span>
                                    </td>
                                    <td>
                                        <button
                                            className="btn-link"
                                            style={{
                                                color: u.storeCount === 0 ? 'var(--color-text-tertiary)' : 'var(--color-primary-light)',
                                                cursor: 'pointer', background: 'none', border: 'none',
                                                fontFamily: 'inherit', fontSize: 'inherit',
                                                textDecoration: u.storeCount > 0 ? 'underline' : 'none',
                                                padding: 0
                                            }}
                                            onClick={() => setStoreModal(u)}
                                            title="Manage store access"
                                        >
                                            {u.storeCount === 0 ? '— Assign' : `${u.storeCount} store${u.storeCount !== 1 ? 's' : ''}`}
                                        </button>
                                    </td>
                                    <td style={{ color: 'var(--color-text-tertiary)', fontSize: 'var(--text-xs)' }}>
                                        {u.lastLoginAt ? new Date(u.lastLoginAt).toLocaleDateString() : 'Never'}
                                    </td>
                                    <td>
                                        <span className={`badge badge-${u.isActive ? 'active' : 'inactive'}`}>
                                            {u.isActive ? 'Active' : 'Inactive'}
                                        </span>
                                    </td>
                                    <td>
                                        <div className="table-actions">
                                            <RowActionsMenu sections={[
                                                [
                                                    { icon: '✏', label: 'Change Role', onClick: () => { setRoleModal(u); setNewRoleId(u.roleId ?? '') } },
                                                    { icon: '🏪', label: 'Manage Store Access', onClick: () => setStoreModal(u) },
                                                    {
                                                        icon: u.isActive ? '⏸' : '▶',
                                                        label: u.isActive ? 'Deactivate' : 'Reactivate',
                                                        onClick: () => setConfirmAction({ kind: u.isActive ? 'deactivate' : 'reactivate', user: u })
                                                    },
                                                ],
                                                [
                                                    { icon: '🗑', label: 'Delete User…', danger: true, onClick: () => setConfirmAction({ kind: 'delete', user: u }) },
                                                ],
                                            ]} />
                                            {useAuthStore.getState().hasPermission('users.impersonate') && u.role !== 'SuperAdmin' && !useAuthStore.getState().isImpersonating && (
                                                <button
                                                    className="btn-icon"
                                                    title="Impersonate this user"
                                                    style={{ color: 'var(--color-primary)' }}
                                                    onClick={() => setConfirmAction({ kind: 'impersonate', user: u })}
                                                >
                                                    🎭
                                                </button>
                                            )}
                                        </div>
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                )}

                {/* Pagination */}
                {totalPages > 1 && (
                    <div style={{
                        display: 'flex', justifyContent: 'space-between', alignItems: 'center',
                        padding: 'var(--space-3) var(--space-5)',
                        borderTop: '1px solid var(--color-border)',
                        fontSize: 'var(--text-sm)', color: 'var(--color-text-secondary)'
                    }}>
                        <span>Page {page} of {totalPages}</span>
                        <div style={{ display: 'flex', gap: 'var(--space-2)' }}>
                            <button className="btn btn-secondary" disabled={page <= 1} onClick={() => setPage(p => p - 1)}>← Prev</button>
                            <button className="btn btn-secondary" disabled={page >= totalPages} onClick={() => setPage(p => p + 1)}>Next →</button>
                        </div>
                    </div>
                )}
            </div>

            {/* Role Change Modal */}
            {roleModal && (
                <div className="modal-backdrop">
                    <div className="modal">
                        <div className="modal-header">
                            <h2>Change Role</h2>
                            <button className="modal-close" onClick={() => setRoleModal(null)}>✕</button>
                        </div>
                        <div className="modal-body">
                            <div style={{
                                display: 'flex', alignItems: 'center', gap: 'var(--space-3)',
                                background: 'var(--color-surface)', borderRadius: 'var(--radius-md)',
                                padding: 'var(--space-3)', marginBottom: 'var(--space-4)'
                            }}>
                                <UserAvatar user={roleModal} />
                                <div>
                                    <div style={{ fontWeight: 600 }}>{roleModal.displayName}</div>
                                    <div style={{ fontSize: 'var(--text-xs)', color: 'var(--color-text-tertiary)' }}>{roleModal.email}</div>
                                </div>
                            </div>
                            <div className="form-field">
                                <label className="form-label">New Role</label>
                                <select
                                    className="form-select"
                                    value={newRoleId}
                                    onChange={e => setNewRoleId(e.target.value)}
                                >
                                    <option value="" disabled>Select a role…</option>
                                    {(assignableRoles ?? []).map(r => <option key={r.id} value={r.id}>{r.name}</option>)}
                                </select>
                            </div>
                            {changeRole.isError && (
                                <p style={{ color: 'var(--color-error)', fontSize: 'var(--text-sm)' }}>
                                    {String((changeRole.error as Error).message)}
                                </p>
                            )}
                        </div>
                        <div className="modal-footer">
                            <button className="btn btn-secondary" onClick={() => setRoleModal(null)}>Cancel</button>
                            <button
                                className="btn btn-primary"
                                disabled={!newRoleId || newRoleId === roleModal.roleId || changeRole.isPending}
                                onClick={() => changeRole.mutate({ userId: roleModal.id, roleId: newRoleId })}
                            >
                                {changeRole.isPending ? 'Saving…' : 'Save Role'}
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {/* Store Assignment + Classification Modal */}
            {storeModal && (
                <div className="modal-backdrop">
                    <div className="modal" style={{ maxWidth: 640 }}>
                        <div className="modal-header">
                            <h2>Manage Store Access</h2>
                            <button className="modal-close" onClick={() => setStoreModal(null)}>✕</button>
                        </div>
                        <div className="modal-body">
                            {/* User info bar */}
                            <div style={{
                                display: 'flex', alignItems: 'center', gap: 'var(--space-3)',
                                background: 'var(--color-surface)', borderRadius: 'var(--radius-md)',
                                padding: 'var(--space-3)', marginBottom: 'var(--space-4)'
                            }}>
                                <UserAvatar user={storeModal} />
                                <div>
                                    <div style={{ fontWeight: 600 }}>{storeModal.displayName}</div>
                                    <div style={{ fontSize: 'var(--text-xs)', color: 'var(--color-text-tertiary)' }}>{storeModal.email}</div>
                                </div>
                                <span className={roleBadgeClass(storeModal.role)} style={{ marginLeft: 'auto' }}>{storeModal.roleName ?? storeModal.role}</span>
                            </div>

                            <div style={{ fontSize: 'var(--text-sm)', fontWeight: 600, color: 'var(--color-text-secondary)', marginBottom: 'var(--space-3)' }}>
                                Toggle stores and assign a classification for each
                            </div>

                            {!allStores || !classificationsData ? (
                                <div style={{ textAlign: 'center', padding: 'var(--space-4)', color: 'var(--color-text-tertiary)' }}>Loading…</div>
                            ) : (
                                <div style={{ maxHeight: 350, overflowY: 'auto' }}>
                                    {allStores.map(store => {
                                        const entry = storeEntries.find(e => e.storeId === store.legacyStoreId)
                                        const isSelected = !!entry
                                        return (
                                            <div
                                                key={store.legacyStoreId}
                                                style={{
                                                    display: 'flex', alignItems: 'center', gap: '0.75rem',
                                                    padding: '0.5rem 0.75rem',
                                                    borderRadius: 'var(--radius-md)',
                                                    marginBottom: '0.25rem',
                                                    background: isSelected ? 'rgba(139, 92, 246, 0.08)' : 'transparent',
                                                    transition: 'background 0.15s'
                                                }}
                                            >
                                                {/* Toggle checkbox */}
                                                <button
                                                    onClick={() => toggleStore(store.legacyStoreId)}
                                                    style={{
                                                        width: 22, height: 22, borderRadius: 4, flexShrink: 0,
                                                        border: `2px solid ${isSelected ? 'var(--color-primary)' : 'var(--color-border)'}`,
                                                        background: isSelected ? 'var(--color-primary)' : 'transparent',
                                                        color: 'white', cursor: 'pointer',
                                                        display: 'flex', alignItems: 'center', justifyContent: 'center',
                                                        fontSize: '0.75rem', fontWeight: 700, lineHeight: 1, padding: 0
                                                    }}
                                                >
                                                    {isSelected ? '✓' : ''}
                                                </button>

                                                {/* Store name */}
                                                <div style={{ flex: 1, minWidth: 0 }}>
                                                    <div style={{ fontWeight: 500, fontSize: 'var(--text-sm)' }}>{store.name}</div>
                                                    {store.city && (
                                                        <div style={{ fontSize: 'var(--text-xs)', color: 'var(--color-text-tertiary)' }}>{store.city}{store.province ? `, ${store.province}` : ''}</div>
                                                    )}
                                                </div>

                                                {/* Classification dropdown — only if selected */}
                                                {isSelected && (
                                                    <select
                                                        className="form-select"
                                                        style={{ width: 'auto', minWidth: 140, fontSize: 'var(--text-xs)', padding: 'var(--space-1) var(--space-2)' }}
                                                        value={entry?.classificationId ?? ''}
                                                        onChange={e => setClassification(store.legacyStoreId, e.target.value || null)}
                                                    >
                                                        <option value="">No classification</option>
                                                        {classifications.map(c => (
                                                            <option key={c.id} value={c.id}>{c.name}</option>
                                                        ))}
                                                    </select>
                                                )}
                                            </div>
                                        )
                                    })}
                                </div>
                            )}

                            <div style={{ marginTop: 'var(--space-3)', fontSize: 'var(--text-xs)', color: 'var(--color-text-tertiary)' }}>
                                {storeEntries.length} store{storeEntries.length !== 1 ? 's' : ''} selected
                            </div>

                            {saveStores.isError && (
                                <p style={{ color: 'var(--color-error)', fontSize: 'var(--text-sm)', marginTop: 'var(--space-2)' }}>
                                    Failed to save store assignments.
                                </p>
                            )}
                        </div>
                        <div className="modal-footer">
                            <button className="btn btn-secondary" onClick={() => setStoreModal(null)}>Cancel</button>
                            <button
                                className="btn btn-primary"
                                disabled={saveStores.isPending}
                                onClick={() => saveStores.mutate({ userId: storeModal.id, stores: storeEntries })}
                            >
                                {saveStores.isPending ? 'Saving…' : 'Save Store Access'}
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {/* Invite dialog. Deliberately has no ✕ and no backdrop click-to-close: the only
                ways out are Send and Cancel, so a half-typed invitation can't be lost by a
                stray click. */}
            {inviteOpen && (
                <div className="modal-backdrop">
                    <div className="modal" style={{ maxWidth: 640 }}>
                        <div className="modal-header">
                            <h2>Invite a user</h2>
                        </div>
                        <div className="modal-body">
                            <p style={{ fontSize: 'var(--text-sm)', color: 'var(--color-text-secondary)', marginBottom: 'var(--space-4)' }}>
                                They'll get an email invitation. The store access below is applied
                                automatically the first time they sign in with their work Google account.
                            </p>

                            <div className="form-field">
                                <label className="form-label">Email address</label>
                                <input
                                    className="form-input"
                                    type="email"
                                    autoFocus
                                    placeholder="name@vpo.ca"
                                    value={inviteEmail}
                                    onChange={e => { setInviteEmail(e.target.value); setInviteError(null) }}
                                />
                            </div>

                            <div style={{ fontSize: 'var(--text-sm)', fontWeight: 600, color: 'var(--color-text-secondary)', margin: 'var(--space-4) 0 var(--space-3)' }}>
                                Stores they'll be given access to
                            </div>

                            {!allStores || !classificationsData ? (
                                <div style={{ textAlign: 'center', padding: 'var(--space-4)', color: 'var(--color-text-tertiary)' }}>Loading…</div>
                            ) : (
                                <div style={{ maxHeight: 280, overflowY: 'auto' }}>
                                    {allStores.map(store => {
                                        const entry = inviteStores.find(e => e.storeId === store.legacyStoreId)
                                        const isSelected = !!entry
                                        return (
                                            <div
                                                key={store.legacyStoreId}
                                                style={{
                                                    display: 'flex', alignItems: 'center', gap: '0.75rem',
                                                    padding: '0.5rem 0.75rem',
                                                    borderRadius: 'var(--radius-md)',
                                                    marginBottom: '0.25rem',
                                                    background: isSelected ? 'rgba(139, 92, 246, 0.08)' : 'transparent',
                                                    transition: 'background 0.15s'
                                                }}
                                            >
                                                <button
                                                    onClick={() => toggleInviteStore(store.legacyStoreId)}
                                                    style={{
                                                        width: 22, height: 22, borderRadius: 4, flexShrink: 0,
                                                        border: `2px solid ${isSelected ? 'var(--color-primary)' : 'var(--color-border)'}`,
                                                        background: isSelected ? 'var(--color-primary)' : 'transparent',
                                                        color: 'white', cursor: 'pointer',
                                                        display: 'flex', alignItems: 'center', justifyContent: 'center',
                                                        fontSize: '0.75rem', fontWeight: 700, lineHeight: 1, padding: 0
                                                    }}
                                                >
                                                    {isSelected ? '✓' : ''}
                                                </button>

                                                <div style={{ flex: 1, minWidth: 0 }}>
                                                    <div style={{ fontWeight: 500, fontSize: 'var(--text-sm)' }}>{store.name}</div>
                                                    {store.city && (
                                                        <div style={{ fontSize: 'var(--text-xs)', color: 'var(--color-text-tertiary)' }}>{store.city}{store.province ? `, ${store.province}` : ''}</div>
                                                    )}
                                                </div>

                                                {isSelected && (
                                                    <select
                                                        className="form-select"
                                                        style={{ width: 'auto', minWidth: 140, fontSize: 'var(--text-xs)', padding: 'var(--space-1) var(--space-2)' }}
                                                        value={entry?.classificationId ?? ''}
                                                        onChange={e => setInviteClassification(store.legacyStoreId, e.target.value || null)}
                                                    >
                                                        <option value="">No classification</option>
                                                        {classifications.map(c => (
                                                            <option key={c.id} value={c.id}>{c.name}</option>
                                                        ))}
                                                    </select>
                                                )}
                                            </div>
                                        )
                                    })}
                                </div>
                            )}

                            <div style={{ marginTop: 'var(--space-3)', fontSize: 'var(--text-xs)', color: 'var(--color-text-tertiary)' }}>
                                {inviteStores.length} store{inviteStores.length !== 1 ? 's' : ''} selected
                            </div>

                            <div className="form-field" style={{ marginTop: 'var(--space-4)' }}>
                                <label className="form-label">Personal note (optional)</label>
                                <textarea
                                    className="form-textarea"
                                    rows={2}
                                    placeholder="Welcome to the team!"
                                    value={inviteMessage}
                                    onChange={e => setInviteMessage(e.target.value)}
                                />
                            </div>

                            {inviteError && (
                                <p style={{ color: 'var(--color-error)', fontSize: 'var(--text-sm)', marginTop: 'var(--space-2)' }}>
                                    {inviteError}
                                </p>
                            )}
                        </div>
                        <div className="modal-footer">
                            <button className="btn btn-secondary" onClick={closeInvite}>Cancel</button>
                            <button
                                className="btn btn-primary"
                                disabled={!inviteEmail.trim() || inviteStores.length === 0 || createInvite.isPending}
                                onClick={() => { setInviteError(null); createInvite.mutate() }}
                            >
                                {createInvite.isPending ? 'Sending…' : 'Send invitation'}
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {confirmAction && confirmAction.kind === 'revokeInvite' && (
                <ConfirmDialog
                    title="Revoke invitation?"
                    message={`Revoke the invitation to ${confirmAction.invitation.email}? The link stops working and they will not get access if they sign in. You can invite them again later.`}
                    confirmLabel="Revoke invitation"
                    danger
                    onCancel={() => setConfirmAction(null)}
                    onConfirm={() => {
                        setActionError(null)
                        revokeInvite.mutate(confirmAction.invitation)
                        setConfirmAction(null)
                    }}
                />
            )}

            {confirmAction && confirmAction.kind !== 'revokeInvite' && (() => {
                const u = confirmAction.user
                const cfg = {
                    impersonate: {
                        title: 'Impersonate user?',
                        message: `Impersonate ${u.displayName}? You will be able to perform actions as this user. All actions will be logged.`,
                        confirmLabel: 'Impersonate', danger: false,
                        run: () => impersonate.mutate(u),
                    },
                    deactivate: {
                        title: 'Deactivate user?',
                        message: `Deactivate ${u.displayName}? They will not be able to log in.`,
                        confirmLabel: 'Deactivate', danger: false,
                        run: () => deactivate.mutate(u.id),
                    },
                    reactivate: {
                        title: 'Reactivate user?',
                        message: `Reactivate ${u.displayName}? They will be able to log in again.`,
                        confirmLabel: 'Reactivate', danger: false,
                        run: () => reactivate.mutate(u.id),
                    },
                    delete: {
                        title: 'Permanently delete user?',
                        message: `Permanently delete ${u.displayName} and all their data? They would need to re-register to access the site again. This cannot be undone.`,
                        confirmLabel: 'Delete permanently', danger: true,
                        run: () => deleteUser.mutate(u.id),
                    },
                }[confirmAction.kind]
                return (
                    <ConfirmDialog
                        title={cfg.title}
                        message={cfg.message}
                        confirmLabel={cfg.confirmLabel}
                        danger={cfg.danger}
                        onCancel={() => setConfirmAction(null)}
                        onConfirm={() => { setActionError(null); cfg.run(); setConfirmAction(null) }}
                    />
                )
            })()}
        </>
    )
}

